import React from 'react'
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis, PieChart, Pie, Cell } from 'recharts';

// Liquid glass card wrapper
const GlassCard = ({ children, glowColor = 'orange' }) => {
    const glowMap = {
        orange: 'shadow-orange-500/10 hover:shadow-orange-500/20 border-orange-400/20',
        blue: 'shadow-blue-500/10 hover:shadow-blue-500/20 border-blue-400/20',
        green: 'shadow-emerald-500/10 hover:shadow-emerald-500/20 border-emerald-400/20',
        purple: 'shadow-purple-500/10 hover:shadow-purple-500/20 border-purple-400/20',
    };
    return (
        <div className={`
            relative overflow-hidden rounded-2xl p-4 md:p-5
            bg-white/4 backdrop-blur-xl
            border border-white/8
            shadow-xl ${glowMap[glowColor] || glowMap.orange}
            transition-all duration-500
        `}>
            {/* Top glass reflection */}
            <div className="absolute inset-x-0 top-0 h-px bg-linear-to-r from-transparent via-white/20 to-transparent" />
            {/* Inner glow */}
            <div className="absolute -top-20 -right-20 w-40 h-40 rounded-full bg-white/3 blur-3xl pointer-events-none" />
            <div className="relative z-10">{children}</div>
        </div>
    );
};

// Dark tooltip styles
const darkTooltipStyle = {
    backgroundColor: 'rgba(15, 23, 42, 0.9)',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: '12px',
    fontSize: '12px',
    color: '#fff',
    backdropFilter: 'blur(12px)',
    boxShadow: '0 8px 32px rgba(0,0,0,0.3)'
};
const darkTextStyle = { color: '#fff' };

export const PersonalTrainerCharts = () => {
    const trainerCostTrend = [
        { age: '15-25', cost: 80, label: 'Young Adults' },
        { age: '25-35', cost: 100, label: 'Working Adults' },
        { age: '35-45', cost: 120, label: 'Mid-Career' },
        { age: '45-60', cost: 130, label: 'Senior Professionals' },
        { age: '60+', cost: 80, label: 'Retirees' }
    ];

    const residentPercent = [
        { age: '15-25', percent: 15 },
        { age: '25-35', percent: 40 },
        { age: '35-45', percent: 45 },
        { age: '45-60', percent: 30 },
        { age: '60+', percent: 20 }
    ];

    const trainerTypeData = [
        { name: 'Freelance PT', value: 62, color: '#10B981' },
        { name: 'Gym PT', value: 28, color: '#F59E0B' },
        { name: 'Online PT', value: 10, color: '#3B82F6' }
    ];

    const trainingLocationData = [
        { location: 'Condo Gym', percentage: 30 },
        { location: 'Commercial Gym', percentage: 25 },
        { location: 'ActiveSG', percentage: 20 },
        { location: 'Home', percentage: 12 },
        { location: 'Outdoor/Park', percentage: 8 },
        { location: 'Online', percentage: 5 }
    ];

    const axisTickStyle = { fontSize: 11, fill: 'rgba(255,255,255,0.5)' };
    const gridStroke = 'rgba(255,255,255,0.06)';

  return (
    <section className='max-w-5xl mx-auto py-10 px-4'>
        <header className='mb-8'>
            <h2 className='px-2 text-xl md:text-2xl text-center font-bold uppercase text-white'>Personal Trainer Cost Statistics in Singapore (2025-2026)</h2>
            <p className='text-center text-white/40 mt-2'>Indicative trends based on StringsSG booking and enquiry data</p>
            <p className='text-center text-white/30 text-xs mt-1 max-w-2xl mx-auto'>
                Figures below reflect activity on the StringsSG platform and are shown as directional guidance, not a nationally representative survey of Singapore.
            </p>
        </header>    
        
        <div className='grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6'>
            {/* Cost by Age Group */}
            <GlassCard glowColor="orange">
                <h3 className='text-sm md:text-base font-bold text-orange-400 text-center mb-2'>💰 Average PT Cost by Age Group</h3>
                <p className='text-xs md:text-sm text-white/40 text-center mb-3 md:mb-4'>
                    How much different age groups typically pay per session
                </p>
                <div className='h-48 md:h-56'>
                    <ResponsiveContainer width='100%' height='100%'>
                        <BarChart data={trainerCostTrend}>
                            <CartesianGrid strokeDasharray='3 3' stroke={gridStroke} />
                            <XAxis dataKey='age' tick={axisTickStyle} axisLine={{ stroke: 'rgba(255,255,255,0.1)' }} />
                            <YAxis tick={axisTickStyle} tickFormatter={(v) => `$${v}`} axisLine={{ stroke: 'rgba(255,255,255,0.1)' }} />
                            <Tooltip
                                formatter={(v) => [`S$${v}/session`, 'Average Cost']}
                                contentStyle={darkTooltipStyle}
                                itemStyle={darkTextStyle}
                                labelStyle={darkTextStyle}
                                cursor={{ fill: 'rgba(255,255,255,0.03)' }}
                            />
                            <Bar dataKey='cost' fill='url(#costGradient)' radius={[6, 6, 0, 0]} />
                            <defs>
                                <linearGradient id='costGradient' x1='0' y1='0' x2='0' y2='1'>
                                    <stop offset='5%' stopColor='#FF6B35' stopOpacity={0.9}/>
                                    <stop offset='95%' stopColor='#F7931E' stopOpacity={0.6}/>
                                </linearGradient>
                            </defs>
                        </BarChart>
                    </ResponsiveContainer>
                </div>
                <p className='text-xs text-white/30 mt-2 text-center'>
                    📈 The 45-60 age group pays the most ($130/session), typically for health-focused goals
                </p>
            </GlassCard>

            {/* Demand by Age Group */}
            <GlassCard glowColor="blue">
                <h3 className='text-sm md:text-base font-bold text-blue-400 text-center mb-2'>📊 PT Demand by Age Group</h3>
                <p className='text-xs md:text-sm text-white/40 text-center mb-3 md:mb-4'>
                    Percentage considering personal training in each age bracket
                </p>
                <div className='h-48 md:h-56'>
                    <ResponsiveContainer width='100%' height='100%'>
                        <BarChart data={residentPercent}>
                            <defs>
                                <linearGradient id="percentGradient" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.9}/>
                                    <stop offset="95%" stopColor="#1D4ED8" stopOpacity={0.6}/>
                                </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray='3 3' stroke={gridStroke} />
                            <XAxis dataKey='age' tick={axisTickStyle} axisLine={{ stroke: 'rgba(255,255,255,0.1)' }} />
                            <YAxis tick={axisTickStyle} tickFormatter={(v) => `${v}%`} axisLine={{ stroke: 'rgba(255,255,255,0.1)' }} />
                            <Tooltip 
                                formatter={(v) => [`${v}%`, 'Considering PT']} 
                                contentStyle={darkTooltipStyle}
                                itemStyle={darkTextStyle}
                                labelStyle={darkTextStyle}
                                cursor={{ fill: 'rgba(255,255,255,0.03)' }}
                            />
                            <Bar dataKey='percent' fill='url(#percentGradient)' radius={[6, 6, 0, 0]} />
                        </BarChart>
                    </ResponsiveContainer>
                </div>
                <p className='text-xs text-white/30 mt-2 text-center'>
                    📈 35-45 age group has highest demand (45%) – career stability + health awareness
                </p>
            </GlassCard>

            {/* Trainer Type Preference */}
            <GlassCard glowColor="green">
                <h3 className='text-sm md:text-base font-bold text-emerald-400 text-center mb-2'>👤 Trainer Type Preference</h3>
                <p className='text-xs md:text-sm text-white/40 text-center mb-3 md:mb-4'>
                    How Singaporeans choose their trainers
                </p>
                <div className='h-40 md:h-48'>
                    <ResponsiveContainer width='100%' height='100%'>
                        <PieChart>
                            <Pie
                                data={trainerTypeData}
                                cx='50%'
                                cy='50%'
                                innerRadius={35}
                                outerRadius={60}
                                paddingAngle={3}
                                dataKey='value'
                                stroke='rgba(255,255,255,0.1)'
                                strokeWidth={1}
                            >
                                {trainerTypeData.map((entry, index) => (
                                    <Cell key={`cell-${index}`} fill={entry.color} fillOpacity={0.8} />
                                ))}
                            </Pie>
                            <Tooltip 
                                formatter={(v) => [`${v}%`, 'Market Share']} 
                                contentStyle={darkTooltipStyle}
                                itemStyle={darkTextStyle}
                                labelStyle={darkTextStyle}
                            />
                        </PieChart>
                    </ResponsiveContainer>
                </div>
                {/* Legend below chart */}
                <div className='flex flex-wrap justify-center gap-3 mt-2'>
                    {trainerTypeData.map((entry, index) => (
                        <div key={index} className='flex items-center text-xs text-white/60'>
                            <div className='w-3 h-3 rounded-full mr-1.5' style={{ backgroundColor: entry.color }}></div>
                            <span>{entry.name}: {entry.value}%</span>
                        </div>
                    ))}
                </div>
                <p className='text-xs text-white/30 mt-2 text-center'>
                    ✅ 62% prefer freelance trainers – more affordable & flexible
                </p>
            </GlassCard>

            {/* Training Location */}
            <GlassCard glowColor="purple">
                <h3 className='text-sm md:text-base font-bold text-purple-400 text-center mb-2'>📍 Preferred Training Locations</h3>
                <p className='text-xs md:text-sm text-white/40 text-center mb-3 md:mb-4'>
                    Where Singaporeans prefer to train with their PTs
                </p>
                <div className='h-48 md:h-56'>
                    <ResponsiveContainer width='100%' height='100%'>
                        <BarChart data={trainingLocationData} layout="vertical">
                            <defs>
                                <linearGradient id="locationGradient" x1="0" y1="0" x2="1" y2="0">
                                    <stop offset="5%" stopColor="#8B5CF6" stopOpacity={0.9}/>
                                    <stop offset="95%" stopColor="#A78BFA" stopOpacity={0.6}/>
                                </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray='3 3' stroke={gridStroke} />
                            <XAxis type="number" tick={{ fontSize: 10, fill: 'rgba(255,255,255,0.5)' }} tickFormatter={(v) => `${v}%`} axisLine={{ stroke: 'rgba(255,255,255,0.1)' }} />
                            <YAxis type="category" dataKey='location' tick={{ fontSize: 9, fill: 'rgba(255,255,255,0.5)' }} width={70} axisLine={{ stroke: 'rgba(255,255,255,0.1)' }} />
                            <Tooltip 
                                formatter={(v) => [`${v}%`, 'Preference']} 
                                contentStyle={darkTooltipStyle}
                                itemStyle={darkTextStyle}
                                labelStyle={darkTextStyle}
                                cursor={{ fill: 'rgba(255,255,255,0.03)' }}
                            />
                            <Bar dataKey='percentage' fill='url(#locationGradient)' radius={[0, 6, 6, 0]} />
                        </BarChart>
                    </ResponsiveContainer>
                </div>
                <p className='text-xs text-white/30 mt-2 text-center'>
                    🏠 Condo gyms lead (35%) – convenience without membership fees
                </p>
            </GlassCard>
        </div>

        {/* Key Insights Summary - Glass bar */}
        <div className='mt-8 relative overflow-hidden rounded-2xl p-5 bg-white/4 backdrop-blur-xl border border-white/8 shadow-xl'>
            <div className="absolute inset-x-0 top-0 h-px bg-linear-to-r from-transparent via-white/20 to-transparent" />
            <div className="absolute -top-20 left-1/2 -translate-x-1/2 w-60 h-40 rounded-full bg-orange-500/5 blur-3xl pointer-events-none" />
            <div className="relative z-10">
                <h3 className='text-lg font-bold text-white mb-4 text-center'>📌 Key Market Insights</h3>
                <div className='grid grid-cols-2 md:grid-cols-4 gap-4'>
                    <div className='text-center p-3 bg-white/5 rounded-xl border border-white/8'>
                        <p className='text-2xl font-bold text-orange-400'>$106</p>
                        <p className='text-xs text-white/40'>Average session cost</p>
                    </div>
                    <div className='text-center p-3 bg-white/5 rounded-xl border border-white/8'>
                        <p className='text-2xl font-bold text-blue-400'>35-45</p>
                        <p className='text-xs text-white/40'>Peak demand age group</p>
                    </div>
                    <div className='text-center p-3 bg-white/5 rounded-xl border border-white/8'>
                        <p className='text-2xl font-bold text-emerald-400'>62%</p>
                        <p className='text-xs text-white/40'>Prefer freelance PTs</p>
                    </div>
                    <div className='text-center p-3 bg-white/5 rounded-xl border border-white/8'>
                        <p className='text-2xl font-bold text-purple-400'>30%</p>
                        <p className='text-xs text-white/40'>Train at condo gyms</p>
                    </div>
                </div>
            </div>
        </div>
    </section>
  )
}
